import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual, isNil } from 'lodash';

import {
  CustomerDocumentTypeEnum,
  CustomerHistoryDetailsModel,
  FullCustomerInfoResponseModel,
} from 'api/adminApiClient';
import BaseAlert from 'components/wrappers/BaseAlert';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getCustomerHistoryFile } from 'redux/reducers/clientSlice';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import {
  getCustomerBreakInServiceLabel,
  getCustomerPreScreeningBilledLabel,
} from 'utils/clientUtils';
import { getWebsiteLink, getWebsiteName } from 'utils/urlUtils';
import { getYesNoAnswer } from 'components/ActivitiesDetailsSection/HistorySection/utils';
import HistoryLog from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryLog';
import HistoryField from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryField';

import styles from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryCard.module.less';

type Props = {
  customerId: string;
  historyDetails: CustomerHistoryDetailsModel;
  historyId: string;
};

export default function AdditionalInformation3DetailsCard(props: Props) {
  const { customerId, historyDetails, historyId } = props;
  const { oldData, newData } = historyDetails;
  const dispatch = useAppDispatch();
  const hasAssignmentRequirementsChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements,
  );
  const hasSpecialAssignmentRequirementsChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.specialAssignmentRequirements,
    newData?.additionalInfo?.assignmentInformation?.specialAssignmentRequirements,
  );
  const hasIsIndependentContractorsChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.isIndependentContractorsOrSubcontractors,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.isIndependentContractorsOrSubcontractors,
  );
  const hasDesignatedPreScreeningProviderChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.designatedPreScreeningProvider,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.designatedPreScreeningProvider,
  );
  const hasIsPreScreeningRequiredChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.isPreScreeningRequired,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.isPreScreeningRequired,
  );
  const hasPreScreeningBilledToChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.preScreeningBilledTo,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.preScreeningBilledTo,
  );
  const hasPreScreeningChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.contractBackgroundScreenRequirements,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.contractBackgroundScreenRequirements,
  );
  const hasIsOnboardingDocumentationRequiredChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.isOnboardingDocumentationRequired,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.isOnboardingDocumentationRequired,
  );
  const hasSpecialInstructionsChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.specialInstructions,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.specialInstructions,
  );
  const hasOnboardingDocumentationFilesChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.onboardingDocumentationFileKeys,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.onboardingDocumentationFileKeys,
  );
  const hasIsDrugScreensRequiredChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.isDrugScreensRequired,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.isDrugScreensRequired,
  );
  const hasDrugScreenRequirementsChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.drugScreenRequirements,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.drugScreenRequirements,
  );
  const hasIsCredentialingRequiredChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.isCredentialingRequired,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.isCredentialingRequired,
  );
  const hasCredentialingRequirementsChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.credentialingRequirements,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.credentialingRequirements,
  );
  const hasIsSpecialRatesDefinedChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.isSpecialRatesDefined,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.isSpecialRatesDefined,
  );
  const hasSpecialRateRequirementsChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.specialRateRequirements,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.specialRateRequirements,
  );
  const hasCspNotesChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.cspNotes,
    newData?.additionalInfo?.assignmentInformation?.assignmentRequirements
      ?.cspNotes,
  );
  const hasGuaranteedWorkPerformanceChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.specialAssignmentRequirements
      ?.guaranteedWorkPerformance,
    newData?.additionalInfo?.assignmentInformation?.specialAssignmentRequirements
      ?.guaranteedWorkPerformance,
  );
  const hasTimeEntryChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.specialAssignmentRequirements
      ?.timeEntry,
    newData?.additionalInfo?.assignmentInformation?.specialAssignmentRequirements
      ?.timeEntry,
  );
  const hasAddedNotesChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.specialAssignmentRequirements
      ?.timecardSpecialInstructions,
    newData?.additionalInfo?.assignmentInformation?.specialAssignmentRequirements
      ?.timecardSpecialInstructions,
  );
  const hasBreakInServiceChanged = !isEqual(
    oldData?.additionalInfo?.assignmentInformation?.specialAssignmentRequirements
      ?.breakInService,
    newData?.additionalInfo?.assignmentInformation?.specialAssignmentRequirements
      ?.breakInService,
  );

  const handleOnboardingDocumentationFileDownload = async (
    fileKey?: string,
  ) => {
    const result = await dispatch(
      getCustomerHistoryFile({
        customerId,
        fileKey,
        documentType: CustomerDocumentTypeEnum.OnboardingDocumentation,
      }),
    );

    if (getCustomerHistoryFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const renderAssignmentRequirementsSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'assignment-requirements';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Assignment requirements</div>
          </Col>

          {hasIsIndependentContractorsChanged && (
            <HistoryField
              title="Are independent contractors or subcontractors permitted?"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.assignmentRequirements
                  ?.isIndependentContractorsOrSubcontractors,
              )}
              dataId={`${sectionName}-independent-contractors-permitted-field`}
            />
          )}

          {hasDesignatedPreScreeningProviderChanged && (
            <HistoryField
              title="Designated pre-screening provider"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.assignmentRequirements?.designatedPreScreeningProvider,
              )}
              dataId={`${sectionName}-designated-pre-screening-provider-field`}
            />
          )}

          {hasIsPreScreeningRequiredChanged && (
            <HistoryField
              title="Pre-screening required"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.assignmentRequirements?.isPreScreeningRequired,
              )}
              dataId={`${sectionName}-pre-screening-required-field`}
            />
          )}

          {hasPreScreeningBilledToChanged && (
            <HistoryField
              title="Pre-screening billed to"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.assignmentInformation
                    ?.assignmentRequirements?.preScreeningBilledTo,
                )
                  ? getCustomerPreScreeningBilledLabel(
                      customerDetails?.additionalInfo?.assignmentInformation
                        ?.assignmentRequirements?.preScreeningBilledTo,
                    )
                  : undefined
              }
              dataId={`${sectionName}-pre-screening-billed-to-field`}
            />
          )}

          {hasPreScreeningChanged && (
            <HistoryField
              title="Contract background screen requirements"
              value={
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.assignmentRequirements?.contractBackgroundScreenRequirements
              }
              dataId={`${sectionName}-pre-screening-field`}
            />
          )}

          {hasIsOnboardingDocumentationRequiredChanged && (
            <HistoryField
              title="Onboarding documentation required"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.assignmentRequirements?.isOnboardingDocumentationRequired,
              )}
              dataId={`${sectionName}-onboarding-documentation-required-field`}
            />
          )}

          {hasSpecialInstructionsChanged && (
            <HistoryField
              title="Special instructions"
              value={
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.assignmentRequirements?.specialInstructions
              }
              dataId={`${sectionName}-special-instructions-field`}
            />
          )}

          {hasOnboardingDocumentationFilesChanged && (
            <HistoryField
              title="Onboarding documentation"
              emptyText="No files"
              value={
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.assignmentRequirements?.onboardingDocumentationFileKeys
                  ?.length &&
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.assignmentRequirements?.onboardingDocumentationFileNames
                  ?.length && (
                  <>
                    {customerDetails.additionalInfo?.assignmentInformation.assignmentRequirements.onboardingDocumentationFileKeys
                      .map<React.ReactNode>((fileKey: string, index: number) => (
                        <span
                          key={index}
                          className="link-imitation"
                          onClick={() =>
                            handleOnboardingDocumentationFileDownload(fileKey)
                          }
                        >
                          {getFileNameWithoutExt(
                            (
                              customerDetails?.additionalInfo?.assignmentInformation
                                ?.assignmentRequirements
                                ?.onboardingDocumentationFileNames as string[]
                            )[index],
                          )}
                        </span>
                      ))
                      .reduce(
                        (
                          previous: React.ReactNode,
                          current: React.ReactNode,
                        ) => [previous, ', ', current],
                      )}
                  </>
                )
              }
              dataId={`${sectionName}-onboarding-documentation-field`}
            />
          )}

          {hasIsDrugScreensRequiredChanged && (
            <HistoryField
              title="Drug screens required?"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.assignmentRequirements?.isDrugScreensRequired,
              )}
              dataId={`${sectionName}-drug-screens-required-field`}
            />
          )}
          {hasDrugScreenRequirementsChanged && (
            <HistoryField
              title="Drug screen requirements"
              value={
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.assignmentRequirements?.drugScreenRequirements
              }
              dataId={`${sectionName}-drug-screen-requirements-field`}
            />
          )}
          {hasIsCredentialingRequiredChanged && (
            <HistoryField
              title="Is credentialing required?"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.assignmentRequirements?.isCredentialingRequired,
              )}
              dataId={`${sectionName}-credentialing-required-field`}
            />
          )}
          {hasCredentialingRequirementsChanged && (
            <HistoryField
              title="Credentialing requirements"
              value={
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.assignmentRequirements?.credentialingRequirements
              }
              dataId={`${sectionName}-credentialing-requirements-field`}
            />
          )}
          {hasIsSpecialRatesDefinedChanged && (
            <HistoryField
              title="Special rates defined?"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.assignmentRequirements?.isSpecialRatesDefined,
              )}
              dataId={`${sectionName}-special-rates-defined-field`}
            />
          )}
          {hasSpecialRateRequirementsChanged && (
            <HistoryField
              title="Special rate requirements"
              value={
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.assignmentRequirements?.specialRateRequirements
              }
              dataId={`${sectionName}-special-rate-requirements-field`}
            />
          )}
          {hasCspNotesChanged && (
            <HistoryField
              title="CSP notes"
              value={
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.assignmentRequirements?.cspNotes ? (
                  <a
                    href={getWebsiteLink(
                      customerDetails?.additionalInfo?.assignmentInformation
                        ?.assignmentRequirements?.cspNotes || '',
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {getWebsiteName(
                      customerDetails?.additionalInfo?.assignmentInformation
                        ?.assignmentRequirements?.cspNotes || '',
                    )}
                  </a>
                ) : undefined
              }
              dataId={`${sectionName}-csp-notes-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderSpecialAssignmentRequirementSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'special-assignment-requirements';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>
              Special assignment requirements
            </div>
          </Col>

          {hasGuaranteedWorkPerformanceChanged && (
            <HistoryField
              title="Guaranteed work performance"
              value={
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.specialAssignmentRequirements?.guaranteedWorkPerformance
              }
              dataId={`${sectionName}-guaranteed-work-performance-field`}
            />
          )}

          {hasTimeEntryChanged && (
            <HistoryField
              title="Time entry method"
              value={
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.specialAssignmentRequirements?.timeEntry
              }
              dataId={`${sectionName}-time-entry-field`}
            />
          )}

          {hasAddedNotesChanged && (
            <HistoryField
              title="Timecard special instructions"
              value={
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.specialAssignmentRequirements?.timecardSpecialInstructions
              }
              dataId={`${sectionName}-added-notes-field`}
            />
          )}

          {hasBreakInServiceChanged && (
            <HistoryField
              title="Break in service"
              value={getCustomerBreakInServiceLabel(
                customerDetails?.additionalInfo?.assignmentInformation
                  ?.specialAssignmentRequirements?.breakInService,
              )}
              dataId={`${sectionName}-break-in-service-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderAdditionalInformation = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    return (
      <>
        {hasAssignmentRequirementsChanged &&
          renderAssignmentRequirementsSection(customerDetails)}

        {hasSpecialAssignmentRequirementsChanged &&
          renderSpecialAssignmentRequirementSection(customerDetails)}
      </>
    );
  };

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderAdditionalInformation(oldData)}
      newData={renderAdditionalInformation(newData)}
    />
  );
}
